<template>
	<!-- AAR-1870: zentrale Obergrenze für die Inhaltsbreite (Wert: --content-max-width in App.vue) -->
	<div class="content-frame" :class="`content-frame--${align}`">
		<template v-if="frame === 'old'">
			<v-container v-if="$slots.header" class="contentFrameHeader" fluid>
				<slot name="header" />
			</v-container>
			<router-view />
		</template>
		<v-container v-else-if="frame !== 'old'" fluid>
			<v-row :dense="!noGutters && dense" :no-gutters="noGutters">
				<v-col v-if="$slots.header" cols="12">
					<slot name="header" />
				</v-col>
				<v-col cols="12">
					<router-view />
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import {
	useContentAlign,
	type ContentAlign,
} from '@/composables/useContentAlign'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
	default: { type: String, default: 'old' },
	dense: { type: Boolean, default: true },
	noGutters: { type: Boolean, default: false },
})

const route = useRoute()
const { contentAlign } = useContentAlign()

/*
 * Die Ausrichtung ist eine Einstellung des Benutzers (AAR-1870) — eine Route
 * darf sie aber überschreiben, wenn die Seite eine Arbeitsfläche ist und keine
 * Leseseite (Belegprüfungs-Cockpit). Bewusst nur lesend: die Einstellung im
 * Menü bleibt unberührt und gilt überall sonst weiter.
 *
 * Bewusst `route.meta` (die zusammengefuehrten Metadaten) statt des letzten
 * `matched`-Eintrags wie bei `frame` unten: eine Route mit `alias` erzeugt
 * einen zweiten, metadatenlosen Eintrag, der beim `pop()` gewinnt — genau das
 * ist dem Cockpit passiert.
 */
const routeAlign = computed(
	() => route.meta?.contentAlign as ContentAlign | undefined
)

const align = computed(() => routeAlign.value ?? contentAlign.value)

const frame = computed(() => {
	const frames = ['old', 'default']
	const value = route.matched
		?.map((match) => match.meta)
		?.filter((meta) => meta)
		?.pop()?.frame

	if (!value) return props.default

	return frames.includes(value) ? value : props.default
})
</script>

<style lang="scss" scoped>
/* Zwischen der Breadcrumb-Zeile und der Seitenueberschrift darunter standen
   32px: zwei aneinandergrenzende <v-container> mit je 16px Padding. Beide
   fallen weg, sobald ein Breadcrumb da ist — die Ueberschrift rueckt direkt
   darunter, ihren Abstand bringt sie selbst mit (v-card-title, 8px).
   Der Header existiert nur, wenn der Slot befuellt ist; ohne Breadcrumb
   greift die Regel also gar nicht und die Seite behaelt ihr Padding.
   Der Folge-Container ist die Seitenkomponente hinter <router-view> und
   liegt damit ausserhalb dieses Scopes — daher :deep(). */
.contentFrameHeader {
	padding-bottom: 0;

	/* .v-col, weil nicht jede Seite einen Container als Wurzel hat —
	   Faqs/Categories/List.vue beginnt direkt mit einer Spalte. */
	& + :deep(.v-container),
	& + :deep(.v-col) {
		padding-top: 0;
	}
}

.content-frame {
	width: 100%;
	max-width: var(--content-max-width, 1440px);
	margin-inline: auto;

	&--left {
		margin-inline: 0;
	}

	&--full {
		max-width: none;
	}
}
</style>
